import { useEffect, useState } from "react";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Drawer from "../../components/Drawer";
import { useApi } from "../../hooks/useApi";
import {
  listDepartments,
  createDepartment,
  updateDepartment,
  deleteDepartment,
} from "../../api/departmentService";
import { paginatedRows } from "../../utils/pagination";
import { showApiError, showToast } from "../../utils/toast";

export default function DepartmentsPage() {
  const [drawer, setDrawer] = useState(null); // null | { mode: 'create' } | { mode: 'edit', dept }
  const depts = useApi(() => listDepartments({ per_page: 100 }));
  const rows = paginatedRows(depts.data);

  const onDelete = async (d) => {
    if (!window.confirm(`Delete department "${d.name}"?`)) return;
    try {
      await deleteDepartment(d.id);
      showToast("success", "Department deleted");
      depts.reload();
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Departments</h1>
          <div className="page-sub">{rows.length} departments</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={() => setDrawer({ mode: "create" })}>
            <Icon name="plus" size={14} /> New department
          </button>
        </div>
      </div>

      {depts.loading ? (
        <LoadingBlock rows={2} height={120} />
      ) : rows.length === 0 ? (
        <EmptyState icon="briefcase" title="No departments" hint="Create departments to organise team members." />
      ) : (
        <div className="grid-3">
          {rows.map((d) => (
            <div key={d.id} className="card">
              <div className="card-body">
                <div className="row gap-sm">
                  <div style={{
                    width: 36, height: 36, borderRadius: 8,
                    background: `hsl(${(d.id * 67) % 360} 70% 95%)`,
                    color: `hsl(${(d.id * 67) % 360} 70% 35%)`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Icon name="briefcase" size={16} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="t-primary fw-600" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {d.name}
                    </div>
                    <div className="t-muted text-xs">{d.employees_count ?? 0} members</div>
                  </div>
                  <div className="row gap-sm">
                    <button
                      className="icon-btn"
                      title="Edit"
                      onClick={() => setDrawer({ mode: "edit", dept: d })}
                    >
                      <Icon name="edit" size={13} />
                    </button>
                    <button
                      className="icon-btn"
                      title="Delete"
                      onClick={() => onDelete(d)}
                      style={{ color: "var(--danger)" }}
                    >
                      <Icon name="trash" size={13} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <DeptDrawer
        open={!!drawer}
        mode={drawer?.mode}
        dept={drawer?.dept}
        onClose={() => setDrawer(null)}
        onSaved={() => {
          setDrawer(null);
          depts.reload();
        }}
      />
    </>
  );
}

function DeptDrawer({ open, mode, dept, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(mode === "edit" && dept ? dept.name : "");
    setErrors({});
  }, [open, mode, dept]);

  const submit = async () => {
    setErrors({});
    setSaving(true);
    try {
      if (mode === "edit") {
        await updateDepartment(dept.id, { name });
        showToast("success", "Department updated");
      } else {
        await createDepartment({ name });
        showToast("success", "Department created");
      }
      onSaved?.();
    } catch (err) {
      if (err?.data?.errors) setErrors(err.data.errors);
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={mode === "edit" ? `Edit ${dept?.name || "department"}` : "New department"}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving || !name.trim()}>
            {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Create"}
          </button>
        </>
      }
    >
      <div className="field">
        <label className="field-label">Name</label>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Audit · GST · Direct Tax" />
        {errors.name?.[0] && <div className="field-error">{errors.name[0]}</div>}
      </div>
    </Drawer>
  );
}
